// src/features/admin/AdminPage.jsx
//
// CANONICAL — active Admin page component.
// VIEW_ADMIN → AdminPage.jsx
// No sub-views (single layout with AdminSubSidebar + active-section panel).
//
// Slice Adm1 (2026-05-12): rewritten from the prior stacked layout
// (MigrationPanel → FxRatesPanel → ExportPanel, all visible at once)
// to a two-column sub-sidebar layout. Left column lists sections;
// right column shows the active section's content.
//
// ADMIN-NAV-EXPAND (2026-06-11): sections regrouped into labelled
// groups (Users & Access, System, Data & Integrations, Portfolio Model,
// Governance). Each section now
// carries a `group` field that AdminSubSidebar uses to render group
// labels at group boundaries.
//
// Section list (order is load-bearing — drives sidebar render order):
//   USERS & ACCESS
//     users_sso      → AdminUsersPanel
//     roles          → RoleManagementPanel
//     activity       → AdminActivityPanel
//   SYSTEM
//     nav_features   → NavFeaturesPanel      (existing)
//     app_branding   → AdminPlaceholderPanel
//     ai_governance  → AdminPlaceholderPanel
//   DATA & INTEGRATIONS
//     data           → DataSection           (existing)
//     integrations   → AdminIntegrationsRegistry
//     notifications_slack → AdminPlaceholderPanel
//   PORTFOLIO MODEL
//     amenities      → AmenityBulkSetPanel   (existing)
//     currency       → FxRatesPanel          (existing)
//     retention_privacy   → AdminPlaceholderPanel
//     markets_location_ids → AdminMarketsMediaPage
//   GOVERNANCE
//     audit          → AdminPlaceholderPanel
//
// activeSection is persisted to localStorage under
// `ripple_admin_active_section_v1` (matches the project's snake_case
// + version-suffix convention). On read we VALIDATE against the current
// section key list and fall back to "currency" if the stored value
// isn't recognised — that way a future rename doesn't strand users
// on a missing section.
//
// Slice S6.3 prop-less invariant preserved (no `onBack` here; NavRail
// owns sidebar navigation).

import React, { useState, useCallback } from "react";
import { T } from "../../app/tokens.js";
import { Card } from "../../components/Card.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { FxRatesPanel } from "./FxRatesPanel.jsx";
import { AdminSubSidebar } from "./AdminSubSidebar.jsx";
import { AdminPlaceholderPanel } from "./AdminPlaceholderPanel.jsx";
import { DataSection } from "./DataSection.jsx";
import { NavFeaturesPanel } from "./NavFeaturesPanel.jsx";
import { AmenityBulkSetPanel } from "./AmenityBulkSetPanel.jsx";
import { RoleManagementPanel } from "./RoleManagementPanel.jsx";
import { BenchmarksPanel } from "./BenchmarksPanel.jsx";
import { AdminPhotosPanel } from "./AdminPhotosPanel.jsx";
import { AuditLogPanel } from "./AuditLogPanel.jsx";
import { AdminMarketsMediaPage } from "./AdminMarketsMediaPage.jsx";
import { PolicyEditorPanel } from "./PolicyEditorPanel.jsx";
import { AdminInsightImagerySection } from "./AdminInsightImagerySection.jsx";
import { RecentlyDeletedPanel } from "./RecentlyDeletedPanel.jsx";
import { RadarSignalNoisePanel } from "./RadarSignalNoisePanel.jsx";
import { AdminIntegrationsRegistry } from "./AdminIntegrationsRegistry.jsx";
import { AdminActivityPanel } from "./AdminActivityPanel.jsx";
import { AdminUsersPanel } from "./AdminUsersPanel.jsx";
import { DataTrustWeightsPanel } from "./DataTrustWeightsPanel.jsx";
import { PortfolioCommandPillarsPanel } from "./PortfolioCommandPillarsPanel.jsx";
import { NonWorkingDaysPanel } from "./NonWorkingDaysPanel.jsx";
import { AdminSectionExplainer } from "./AdminSectionExplainer.jsx";

// Exported so the test file can assert against the same source of truth.
// `group` is the section header label shown in the sidebar; items with the
// same group string are rendered under one group label.
export const ADMIN_SECTIONS = [
  // ── ACCESS ─────────────────────────────────────────────────────────
  { key: "users_sso",            label: "Users",                     group: "Users & Access" },
  { key: "roles",                label: "Roles & Permissions",        group: "Users & Access" },
  { key: "activity",             label: "Activity",                   group: "Users & Access" },
  // ── SYSTEM ─────────────────────────────────────────────────────────
  { key: "nav_features",         label: "Navigation & Features",      group: "System" },
  { key: "app_branding",         label: "App & Branding",             group: "System" },
  { key: "ai_governance",        label: "AI & Governance",            group: "System" },
  // ── RADAR ───────────────────────────────────────────────────────────
  { key: "radar_signal_noise",   label: "Signal & noise",             group: "Radar" },
  // ── DATA & INTEGRATIONS ─────────────────────────────────────────────
  { key: "data",                 label: "Data",                       group: "Data & Integrations" },
  { key: "data_trust",           label: "Portfolio Command",          group: "Data & Integrations" },
  { key: "integrations",         label: "Integrations",               group: "Data & Integrations" },
  // ── PORTFOLIO MODEL ─────────────────────────────────────────────────
  { key: "amenities",            label: "Amenities",                  group: "Portfolio Model" },
  { key: "currency",             label: "Currency Rates",             group: "Portfolio Model" },
  { key: "benchmarks",           label: "Benchmarks",                 group: "Portfolio Model" },
  { key: "non_working_days",     label: "Non-working days",           group: "Portfolio Model" },
  { key: "retention_privacy",    label: "Retention & Privacy",        group: "Portfolio Model" },
  { key: "markets_location_ids", label: "Markets & Location IDs",     group: "Portfolio Model" },
  { key: "insight_imagery",      label: "Image Library",              group: "Portfolio Model" },
  // ── GOVERNANCE ──────────────────────────────────────────────────────
  { key: "space_policy",         label: "Space Policy",               group: "Governance" },
  { key: "audit",                label: "Audit Log",                  group: "Governance" },
  { key: "recently_deleted",     label: "Recently Deleted",           group: "Governance" },
];

const VALID_KEYS = new Set(ADMIN_SECTIONS.map((s) => s.key));
const DEFAULT_KEY = "currency";
export const ADMIN_ACTIVE_SECTION_STORAGE_KEY = "ripple_admin_active_section_v1";

export const HIDDEN_SECTION_KEYS = new Set();

export const ADMIN_SECTION_EXPLAINERS = {
  users_sso: {
    label: "Source",
    values: ["Okta (OIDC)", "Manual roster"],
    purpose: "One Directory of everyone with access to the app. Source, Role, and Last login distinguish account holders from people provisioned before sign-in. For sign-in activity and trends, see Activity.",
  },
  roles: {
    label: "Used by",
    values: ["Access control (app-wide)"],
    purpose: "Maps your Okta groups to the three app roles (Admin, Editor, Viewer). Access is enforced on the server; a mapping change takes effect on the user's next login.",
  },
  activity: {
    label: "Source",
    values: ["usage events (18-month retention)"],
    purpose: "Aggregate product usage and retained successful sign-ins. Sign-ins from before capture began are not reconstructed.",
  },
  nav_features: {
    label: "Used by",
    values: ["App navigation (all users)"],
    purpose: "Controls which items appear in the left and top navigation, plus Preview mode for admins. Turning an item off hides it and redirects its route.",
  },
  data: {
    label: "Used by",
    values: ["Offices, Leases, Reports"],
    purpose: "Import workplace data (leases, headcount, badging) from CSV or PDF, review ingestion history, back up or restore the portfolio, and run maintenance. Files are staged for review before anything changes.",
  },
  data_trust: {
    label: "Used by",
    values: ["Portfolio Command score (Home)"],
    purpose: "Configures which pillars contribute to the Portfolio Command score, their weights, and the source-confidence threshold.",
  },
  integrations: {
    label: "Used by",
    values: ["all inbound/outbound data flows"],
    purpose: "The registry of external systems Ripple connects to - what each does, how it authenticates, and where its credentials live (key names only; secrets stay server-side).",
  },
  currency: {
    label: "Used by",
    values: ["USD KPIs, Lease cost / $/SF, Lease exposure, Reports"],
    purpose: "The saved Live, As-of date, or FY budget basis converts foreign-currency lease and cost figures to USD. Admins can preview a basis before saving it app-wide.",
  },
  non_working_days: {
    label: "Used by",
    values: ["Attendance & Utilization", "Home attendance pane"],
    purpose: "Defines the days Ripple and each country are closed. Attendance and utilization skip these dates entirely - a holiday is removed from the math, never counted as a low or empty day. Set a date Ripple-wide (every office) or under a country (only that country's offices).",
  },
  markets_location_ids: {
    label: "Used by",
    values: ["office/market imagery, cross-system location ID"],
    purpose: "Shows read-only Market and Office photo coverage, links to each record’s Media tab, and manages the canonical location-ID registry, including suggested and external IDs.",
  },
  insight_imagery: {
    label: "Used by",
    values: ["In Focus, Portfolio Command, office pages"],
    purpose: "Manages the images used across app surfaces. An image is chosen by location first, then category, then a neutral default.",
  },
  space_policy: {
    label: "Used by",
    values: ["Capacity planning, Utilization"],
    purpose: "Governed space-planning assumptions - a company baseline plus per-office overrides. The capacity and utilization math picks these up automatically.",
  },
  audit: {
    label: "Source",
    values: ["system (append-only)"],
    purpose: "A read-only record of every state-changing action - lease edits, imports, user and role changes. Every delete and restore is recorded here.",
  },
  recently_deleted: {
    label: "Source",
    values: ["soft-deletes (30-day window)"],
    purpose: "Items deleted in the last 30 days, restorable to where they were; after 30 days they're permanently removed. Admin and editor only.",
  },
};

// Per-section copy for placeholder sections. When a section ships for real,
// drop its entry here and add the real component to the dispatch table below.
const PLACEHOLDER_DESCRIPTIONS = {
  audit:               "Recent admin actions across the portfolio.",
  roles:               "Manage Okta group mappings, sensitivity classifications, and existing role capabilities.",
  app_branding:        "Configure application branding, logos, and colour themes.",
  ai_governance:       "Control AI feature access, model settings, and governance policies.",
  retention_privacy:   "Set data retention schedules and privacy compliance policies.",
  markets_location_ids:"Define markets, manage location ID schemes, and configure geographic hierarchies.",
};

// Keys that render a real (non-placeholder) panel — listed here so the
// dispatch stays explicit and new sections are impossible to accidentally
// skip the placeholder path.
const PLACEHOLDER_KEYS = new Set([
  "ai_governance",
  "retention_privacy",
]);
// benchmarks is NOT in PLACEHOLDER_KEYS — it renders BenchmarksPanel below.

function readInitialSection() {
  try {
    const requested = new URLSearchParams(window.location.search).get("section");
    if (requested === "sensitivity") return "roles";
    if (requested && VALID_KEYS.has(requested)) return requested;
  } catch {
    // URL state is best-effort; fall through to the persisted section.
  }
  try {
    const stored = typeof window !== "undefined"
      ? window.localStorage.getItem(ADMIN_ACTIVE_SECTION_STORAGE_KEY)
      : null;
    // Adm3 migration: "import" and "export" were merged into "data".
    if (stored === "import" || stored === "export") {
      window.localStorage.setItem(ADMIN_ACTIVE_SECTION_STORAGE_KEY, "data");
      return "data";
    }
    if (stored === "connectors") {
      window.localStorage.setItem(ADMIN_ACTIVE_SECTION_STORAGE_KEY, "integrations");
      return "integrations";
    }
    if (stored === "sensitivity") {
      window.localStorage.setItem(ADMIN_ACTIVE_SECTION_STORAGE_KEY, "roles");
      return "roles";
    }
    if (stored === "build_status") {
      window.localStorage.setItem(ADMIN_ACTIVE_SECTION_STORAGE_KEY, "currency");
      return "currency";
    }
    if (stored && VALID_KEYS.has(stored)) return stored;
  } catch {
    // localStorage unavailable (private mode, quota, etc.) — fall through.
  }
  return DEFAULT_KEY;
}

function persistSection(key) {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ADMIN_ACTIVE_SECTION_STORAGE_KEY, key);
    }
  } catch {
    // Silently best-effort; the in-memory state is still correct.
  }
}

function renderSectionContent(activeKey, onOpenImportsQueue, currentRole, onViewDataset, explainer) {
  if (activeKey === "currency")     return <FxRatesPanel currentRole={currentRole} explainer={explainer} />;
  if (activeKey === "nav_features") return <NavFeaturesPanel explainer={explainer} currentRole={currentRole} />;
  if (activeKey === "amenities")    return <AmenityBulkSetPanel />;
  if (activeKey === "benchmarks")   return <BenchmarksPanel currentRole={currentRole} />;
  if (activeKey === "non_working_days") return <NonWorkingDaysPanel explainer={explainer} />;
  if (activeKey === "data")         return <DataSection onOpenImportsQueue={onOpenImportsQueue} currentRole={currentRole} onViewDataset={onViewDataset} explainer={explainer} />;
  if (activeKey === "data_trust")   return <><PortfolioCommandPillarsPanel currentRole={currentRole} explainer={explainer} /><DataTrustWeightsPanel currentRole={currentRole} /></>;
  if (activeKey === "users_sso")    return <AdminUsersPanel explainer={explainer} currentRole={currentRole} />;
  if (activeKey === "roles")        return <RoleManagementPanel explainer={explainer} />;
  if (activeKey === "space_policy")  return <PolicyEditorPanel currentRole={currentRole} explainer={explainer} />;
  if (activeKey === "audit")               return <AuditLogPanel explainer={explainer} />;
  if (activeKey === "markets_location_ids") return <AdminMarketsMediaPage explainer={explainer} />;
  if (activeKey === "app_branding") return <AdminPhotosPanel />;
  if (activeKey === "insight_imagery")    return <AdminInsightImagerySection currentRole={currentRole} explainer={explainer} />;
  if (activeKey === "recently_deleted")  return <RecentlyDeletedPanel explainer={explainer} />;
  if (activeKey === "radar_signal_noise") return <RadarSignalNoisePanel />;
  if (activeKey === "integrations") return <AdminIntegrationsRegistry currentRole={currentRole} explainer={explainer} />;
  if (activeKey === "activity") return <AdminActivityPanel explainer={explainer} />;
  // Placeholders — the dispatch is explicit (rather than a generic
  // fallback) so adding a real implementation later is a one-line edit
  // and the case-list documents the surface.
  if (PLACEHOLDER_KEYS.has(activeKey)) {
    const section = ADMIN_SECTIONS.find((s) => s.key === activeKey);
    return (
      <>
        {section && <h2 style={{ margin: "0 0 10px", color: T.dark, fontSize: T.fsLg5 }}>{section.label}</h2>}
        {explainer}
        <AdminPlaceholderPanel
          sectionKey={activeKey}
          section={section ? section.label : activeKey}
          description={PLACEHOLDER_DESCRIPTIONS[activeKey] || ""}
        />
      </>
    );
  }
  // Defensive — should be unreachable since VALID_KEYS gates state writes.
  return null;
}

export function AdminPage({ onOpenImportsQueue, currentRole, onViewDataset }) {
  const { flags, adminSections } = useFeatureFlagContext();
  const previewMode = !!flags.preview_mode;

  // Sections locked ON in the DB (Role management, Navigation &
  // Features, Audit log) are always visible regardless of their DB `enabled`
  // value — the server also rejects attempts to disable them, so this is
  // belt-and-suspenders, not the source of truth.
  const enabledByKey = React.useMemo(() => {
    const map = {};
    for (const s of adminSections) map[s.key] = s.locked || s.enabled;
    return map;
  }, [adminSections]);

  // Visible section list drives both the sidebar render and the "is this key
  // still reachable" checks below. In preview mode, admins see every section
  // (including hidden ones) so they can validate the effect of a toggle
  // before turning preview mode off; outside preview mode, hidden sections
  // are fully removed from the sidebar and unreachable via direct selection.
  const visibleSections = React.useMemo(() => {
    const base = ADMIN_SECTIONS.filter((s) => !HIDDEN_SECTION_KEYS.has(s.key));
    if (previewMode) return base;
    return base.filter((s) => enabledByKey[s.key] !== false);
  }, [previewMode, enabledByKey]);

  const visibleKeys = React.useMemo(
    () => new Set(visibleSections.map((s) => s.key)),
    [visibleSections]
  );

  const [activeSection, setActiveSection] = useState(readInitialSection);

  // Redirect away from a section that has just become hidden (toggled off in
  // another tab, or on load if the persisted section is no longer visible).
  React.useEffect(() => {
    if (!visibleKeys.has(activeSection)) {
      const fallback = visibleKeys.has(DEFAULT_KEY) ? DEFAULT_KEY : (visibleSections[0]?.key ?? DEFAULT_KEY);
      setActiveSection(fallback);
      persistSection(fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKeys]);

  const onSelect = useCallback((key) => {
    if (!VALID_KEYS.has(key)) return; // ignore unknown keys (defensive)
    if (!visibleKeys.has(key)) return; // ignore hidden sections (guarded server + client side)
    setActiveSection(key);
    persistSection(key);
    try {
      const params = new URLSearchParams(window.location.search);
      params.set("section", key);
      if (key !== "activity") params.delete("userId");
      window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
    } catch {
      // URL state is best-effort; section state remains authoritative.
    }
  }, [visibleKeys]);

  return (
    <div style={{ maxWidth: "1400px", padding: "24px 32px 40px", margin: "0 auto", width: "100%" }}>
      <Card
        style={{
          padding: 0,
          display: "grid",
          gridTemplateColumns: "200px 1fr",
          minHeight: 420,
          overflow: "hidden",
        }}
      >
        <AdminSubSidebar
          sections={visibleSections}
          activeKey={activeSection}
          onSelect={onSelect}
          dimmedKeys={previewMode ? new Set(Object.keys(enabledByKey).filter((k) => enabledByKey[k] === false)) : undefined}
        />
        <div
          data-testid="admin-section-content"
          style={{ padding: "22px 26px", background: T.white, minWidth: 0 }}
        >
          {visibleKeys.has(activeSection)
            ? (
              renderSectionContent(
                activeSection,
                onOpenImportsQueue,
                currentRole,
                onViewDataset,
                ADMIN_SECTION_EXPLAINERS[activeSection]
                  ? <AdminSectionExplainer {...ADMIN_SECTION_EXPLAINERS[activeSection]} />
                  : null
              )
            )
            : null}
        </div>
      </Card>
    </div>
  );
}
